import React, { useState, useEffect } from 'react';
import { Alert, Spinner } from 'reactstrap';

import Content from 'src/layout/content/Content';
import Head from 'src/layout/head/Head';
import { Button } from 'src/components/Component';
import { Auth } from 'aws-amplify';
import Axios from 'axios';
import API_ROUTES from 'src/config/api';
import cssValidator from 'w3c-css-validator';
import { useSelector } from 'react-redux';
import CodeEditor from '@uiw/react-textarea-code-editor';

import { CheckCircleIcon, BellAlertIcon, ChevronDownIcon, ChevronUpIcon, PaintBrushIcon, CreditCardIcon } from "@heroicons/react/24/outline";

const CollapsibleCSSEditor = ({
  title,
  description,
  icon: Icon,
  cssValue,
  onCssChange,
  onSave,
  saving,
  successMessage,
  errorMessage,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm mb-4">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full bg-gradient-to-r from-violet-50 to-violet-100 px-5 py-3 grid grid-cols-3 items-center hover:from-violet-100 hover:to-violet-150 transition-all duration-200 clearfix"
      >
        <div className="flex items-center gap-3 h-auto col-span-2">
          <div className="p-2 bg-violet-200 rounded-lg mr-3">
            <Icon className="h-5 w-5 text-violet-700" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-violet-800">{title}</h3>
            <p className="text-sm text-slate-500">{description}</p>
          </div>
        </div>
        <div className="gap-2 w-full">
          {isExpanded ? (
            <ChevronUpIcon className="h-6 w-6 -mt-1 font-bold text-violet-800 float-right" />
          ) : (
            <ChevronDownIcon className="h-6 w-6 -mt-1 font-bold text-violet-800 float-right" />
          )}
        </div>
      </button>

      <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-5 py-4 bg-white border-t border-slate-100">
          {successMessage && (
            <Alert className="bg-green-50 text-green-800 border border-green-200 rounded-lg mb-4 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="font-normal">{successMessage}</span>
            </Alert>
          )}

          {errorMessage && (
            <Alert className="bg-red-50 text-red-800 border border-red-200 rounded-lg mb-4 flex items-center gap-2">
              <BellAlertIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
              <span className="font-medium">{errorMessage}</span>
            </Alert>
          )}

          <div className="rounded-lg overflow-hidden border border-slate-200">
            <CodeEditor
              language="css"
              value={cssValue || ''}
              onChange={(e) => onCssChange(e.target.value)}
              placeholder="/* Enter your custom CSS here... */

.example-class {
  color: #333;
  font-size: 16px;
}"
              className="min-h-[300px] w-full"
              padding={16}
              data-color-mode="light"
              style={{
                fontSize: 13,
                fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Consolas, Liberation Mono, Menlo, monospace',
                backgroundColor: '#fafafa',
              }}
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              disabled={saving}
              className="btn bg-violet-700 hover:bg-violet-600 text-white px-6 py-3 rounded-lg font-normal text-md transition-colors duration-200 flex items-center gap-2"
              onClick={onSave}
            >
              {saving ? (
                <>
                  <Spinner size="sm" color="light">{''}</Spinner>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Styling = () => {
  const { organisation } = useSelector((state) => state);

  const [paymentFormCss, setPaymentFormCss] = useState('');
  const [paymentPortalCss, setPaymentPortalCss] = useState('');

  const [paymentFormSaving, setPaymentFormSaving] = useState(false);
  const [paymentPortalSaving, setPaymentPortalSaving] = useState(false);

  const [paymentFormSuccess, setPaymentFormSuccess] = useState(null);
  const [paymentFormError, setPaymentFormError] = useState(null);
  const [paymentPortalSuccess, setPaymentPortalSuccess] = useState(null);
  const [paymentPortalError, setPaymentPortalError] = useState(null);

  useEffect(() => {
    setPaymentFormCss(organisation?.defaults?.PAYMENT_CSS || '');
    setPaymentPortalCss(organisation?.defaults?.PORTAL_CSS || '');
  }, [organisation]);

  const clearMessages = (type) => {
    if (type === 'payment-form') {
      setPaymentFormSuccess(null);
      setPaymentFormError(null);
    } else {
      setPaymentPortalSuccess(null);
      setPaymentPortalError(null);
    }
  };

  const saveCss = async (type, cssValue) => {
    const setSaving = type === 'payment-form' ? setPaymentFormSaving : setPaymentPortalSaving;
    const setSuccess = type === 'payment-form' ? setPaymentFormSuccess : setPaymentPortalSuccess;
    const setError = type === 'payment-form' ? setPaymentFormError : setPaymentPortalError;
    const configName = type === 'payment-form' ? 'PAYMENT_CSS' : 'PORTAL_CSS';

    clearMessages(type);
    setSaving(true);

    try {
      const result = await cssValidator.validateText(cssValue || '');

      if (cssValue && result.valid === false) {
        setError('Invalid CSS syntax. Please check your input and try again.');
        setSaving(false);
        return;
      }

      const user = await Auth.currentAuthenticatedUser();

      await Axios(
        API_ROUTES.user['organisation:default:update'](organisation.organisation),
        {
          method: 'post',
          data: {
            name: configName,
            value: cssValue,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      setSuccess('CSS has been validated and saved successfully.');
      setSaving(false);

      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      setError('Failed to save CSS. Please try again.');
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="Styling" />
      <Content>
        <div className="mb-6">
          <p className="text-slate-600">
            Customize the appearance of your payment pages and portal with custom CSS.
            Your styles will be validated before being applied.
          </p>
        </div>

        <CollapsibleCSSEditor
          title="Payment Form"
          description="Customize the appearance of your payment checkout pages"
          icon={CreditCardIcon}
          cssValue={paymentFormCss}
          onCssChange={setPaymentFormCss}
          onSave={() => saveCss('payment-form', paymentFormCss)}
          saving={paymentFormSaving}
          successMessage={paymentFormSuccess}
          errorMessage={paymentFormError}
          defaultExpanded={false}
        />

        <CollapsibleCSSEditor
          title="Payment Portal"
          description="Customize the appearance of your customer payment portal"
          icon={PaintBrushIcon}
          cssValue={paymentPortalCss}
          onCssChange={setPaymentPortalCss}
          onSave={() => saveCss('payment-portal', paymentPortalCss)}
          saving={paymentPortalSaving}
          successMessage={paymentPortalSuccess}
          errorMessage={paymentPortalError}
          defaultExpanded={false}
        />
      </Content>
    </React.Fragment>
  );
};

export default Styling;
